import React, { useState } from "react";
import PropTypes from "prop-types";
import { FormattedMessage } from "react-intl";
import {
  Subtitle1,
  makeStyles,
  Button,
  Label,
  Text,
  Input,
  Divider,
  OverlayDrawer,
  DrawerHeader,
  DrawerHeaderTitle,
  DrawerBody,
  tokens
} from "@fluentui/react-components";
import { ArrowClockwise24Regular, ArrowLeftRegular, CopyRegular } from "@fluentui/react-icons";
import styles from "./EnterOnDeviceModal.scss";
import useBreakpoints from "../hooks/useBreakpoints";
import notificationsCenter from "../../singleton/NotificationsCenter";

const useStyles = makeStyles({
  button: {
    width: "100%",
    marginTop: "12px"
  },
  dialogContent: {
    gap: "8px"
  },
  accessContent: {
    display: "flex",
    flexDirection: "column",
    padding: "5px var(--Horizontal-None, 0px) var(--Font-size-100, 10px) var(--Horizontal-None, 0px)",
    gap: "15px",
    paddingBottom: "16px"
  },
  accessContentBottom: {
    display: "flex",
    flexDirection: "column",
    padding: "var(--Font-size-100, 10px) var(--Horizontal-None, 0px) 5px var(--Horizontal-None, 0px)",
    gap: "15px",
    paddingTop: "16px"
  },
  titleContainer: {
    display: "flex",
    gap: "8px"
  },
  labelAccessVR: {
    fontSize: "18px",
    fontStyle: "normal",
    fontWeight: "600",
    lineHeight: "normal"
  },
  vrContainer: {
    display: "flex",
    flexDirection: "column",
    gap: tokens.spacingVerticalM
  },
  vrContainerInner: {
    display: "flex",
    flexDirection: "column",
    gap: "6px"
  },
  codeContainer: {
    display: "flex",
    flexDirection: "column",
    gap: "2px"
  },
  textCode: {
    color: tokens.colorNeutralForeground1,
    fontSize: " 30px",
    fontStyle: " normal",
    fontWeight: " 700",
    lineHeight: " normal",
    letterSpacing: " 4px",
    overflowWrap: "anywhere"
  },
  codeHint: {
    color: tokens.colorNeutralForeground3,
    marginTop: tokens.spacingVerticalXS
  },
  divider: {
    paddingTop: "5px",
    paddingBottom: "4px"
  }
});

export function EnterOnDevice({
  shortUrl,
  code,
  loadingCode,
  headsetConnected,
  unsupportedBrowser,
  onEnterOnConnectedHeadset,
  attemptLink
}) {
  const classes = useStyles();

  const copyCode = () => {
    notificationsCenter.success("VR Code copied to clipboard");
    navigator.clipboard
      .writeText(shortUrl)
      .then(() => {})
      .catch(err => {
        console.error("Failed to copy: ", err);
      });
  };

  return (
    <>
      <div className={classes.accessContent}>
        <Label className={classes.labelAccessVR}>
          <FormattedMessage id="enter-on-device-modal.heading" defaultMessage="Access in your Headset or Phone" />
        </Label>
        <div className={classes.vrContainer}>
          <div className={classes.vrContainerInner}>
            <Text>
              <FormattedMessage
                id="enter-on-device-modal.short-url-directions"
                defaultMessage="On your headset or phone, go to:"
              />
            </Text>
            <Input
              readOnly
              value={shortUrl}
              contentAfter={<CopyRegular style={{ cursor: "pointer" }} onClick={copyCode} />}
              appearance="filled-darker"
            />
          </div>
          <div className={classes.codeContainer}>
            <Text>
              {!code && !loadingCode ? (
                <FormattedMessage id="enter-on-device-modal.code-expired-heading" defaultMessage="⚠️ Code Expired" />
              ) : (
                <FormattedMessage
                  id="enter-on-device-modal.code-directions"
                  defaultMessage="Enter this private code:"
                />
              )}
            </Text>
            {code && <Text className={classes.textCode}>{code}</Text>}
            {code && (
              <Text className={classes.codeHint} size="200">
                <FormattedMessage
                  id="enter-on-device-modal.private-code-warning"
                  defaultMessage="⚠️ Do not share code with anyone. Keep this window open until connected."
                />
              </Text>
            )}
            {!code && !loadingCode && (
              <div className={classes.vrContainerInner}>
                <Text size="600">
                  <FormattedMessage
                    id="enter-on-device-modal.code-expired-message"
                    defaultMessage="Generate a new code to link your device."
                  />
                </Text>
                <Button appearance="primary" icon={<ArrowClockwise24Regular />} onClick={attemptLink}>
                  <FormattedMessage id="enter-on-device-modal.generate-new-code" defaultMessage="Generate New Code" />
                </Button>
              </div>
            )}
          </div>
        </div>
      </div>
      {headsetConnected && !unsupportedBrowser && (
        <>
          <Divider className={classes.divider} />
          <div className={classes.accessContentBottom}>
            <Label className={classes.labelAccessVR}>
              <FormattedMessage
                id="enter-on-device-modal.headset-connected-heading"
                defaultMessage="Access through a connected headset"
              />
            </Label>
            <Text>
              <FormattedMessage
                id="enter-on-device-modal.headset-connected-message"
                defaultMessage="Select the button if your VR headset is connected to this device."
              />
            </Text>
          </div>
          <Button className={classes.button} appearance="primary" onClick={onEnterOnConnectedHeadset}>
            <FormattedMessage id="enter-on-device-modal.enter-in-vr-button" defaultMessage="Join in VR" />
          </Button>
        </>
      )}
    </>
  );
}

export function EnterOnDeviceModal({
  shortUrl,
  loadingCode,
  code,
  headsetConnected,
  unsupportedBrowser,
  onEnterOnConnectedHeadset,
  onBack,
  attemptLink
}) {
  const classes = useStyles();
  const breakpoint = useBreakpoints();
  const [open, setIsOpen] = useState(true);

  const callbackOpen = async cb => {
    setIsOpen(false);
    setTimeout(() => {
      cb();
    }, 100);
  };

  return (
    <OverlayDrawer
      position={["xs", "sm", "md"].includes(breakpoint) ? "bottom" : "end"}
      open={open}
      modalType="non-modal"
      className={styles.entryCard}
    >
      <DrawerHeader>
        <DrawerHeaderTitle style={{ width: "100%" }}>
          <Button
            onClick={() => callbackOpen(onBack)}
            style={{ height: "30px", width: "30px", marginRight: "10px" }}
            icon={<ArrowLeftRegular style={{ width: "18px" }} />}
          ></Button>
          <Subtitle1>
            <FormattedMessage id="enter-on-device-modal.title" defaultMessage="Join in VR" />
          </Subtitle1>
        </DrawerHeaderTitle>
      </DrawerHeader>
      <DrawerBody className={classes.dialogContent}>
        <EnterOnDevice
          shortUrl={shortUrl}
          loadingCode={loadingCode}
          code={code}
          headsetConnected={headsetConnected}
          unsupportedBrowser={unsupportedBrowser}
          onEnterOnConnectedHeadset={() => callbackOpen(onEnterOnConnectedHeadset)}
          attemptLink={attemptLink}
        />
      </DrawerBody>
    </OverlayDrawer>
  );
}

EnterOnDeviceModal.propTypes = {
  shortUrl: PropTypes.string.isRequired,
  loadingCode: PropTypes.bool,
  code: PropTypes.string,
  headsetConnected: PropTypes.bool,
  unsupportedBrowser: PropTypes.bool,
  onEnterOnConnectedHeadset: PropTypes.func,
  onBack: PropTypes.func,
  attemptLink: PropTypes.func
};

EnterOnDevice.propTypes = {
  shortUrl: PropTypes.string.isRequired,
  loadingCode: PropTypes.bool,
  code: PropTypes.string,
  headsetConnected: PropTypes.bool,
  unsupportedBrowser: PropTypes.bool,
  onEnterOnConnectedHeadset: PropTypes.func,
  attemptLink: PropTypes.func
};
